import { AnnotationLink } from './AnnotationLink';
import type { AnnotationLinkOrigin } from './AnnotationLinkOrigin';
import type { AnnotationLinkId, AnnotationLinkJSON, ModelId, ViewModelId } from 'balus-common';
import { RTDBPath } from 'balus-common';
import type { LinkableTargetKey } from '@view-model/domain/key';
import type { ICommand } from '@model-framework/command';
import { CommandHelper, CompositeCommand } from '@model-framework/command';

export type AnnotationLinkCollectionJSON = AnnotationLinkJSON[];

export class AnnotationLinkCollection {
  private readonly links: Readonly<AnnotationLink[]>;

  constructor(links: AnnotationLink[]) {
    this.links = links;
  }

  static buildEmpty(): AnnotationLinkCollection {
    return new AnnotationLinkCollection([]);
  }

  static load(dump: AnnotationLinkCollectionJSON): AnnotationLinkCollection {
    return new AnnotationLinkCollection(
      dump.flatMap((json) => {
        try {
          return [AnnotationLink.load(json)];
        } catch (e) {
          console.error('AnnotationLink.load() failed');
          return [];
        }
      })
    );
  }

  dump(): AnnotationLinkCollectionJSON {
    return this.links.map((link) => link.dump());
  }

  isEqual(other: AnnotationLinkCollection): boolean {
    return this.count() === other.count() && this.all().every((link) => other.get(link.id)?.isEqual(link));
  }

  toRecord(): Record<AnnotationLinkId, AnnotationLink> {
    return Object.fromEntries(this.links.map((link) => [link.id, link]));
  }

  count(): number {
    return this.links.length;
  }

  isEmpty(): boolean {
    return this.count() === 0;
  }

  all(): AnnotationLink[] {
    return this.links.concat();
  }

  allIds(): AnnotationLinkId[] {
    return this.links.map(({ id }) => id);
  }

  get(id: AnnotationLinkId): AnnotationLink | undefined {
    return this.links.find((link) => link.id === id);
  }

  filterByIds(ids: AnnotationLinkId[]): AnnotationLinkCollection {
    return new AnnotationLinkCollection(this.links.filter(({ id }) => ids.includes(id)));
  }

  filterConnectedTo(fromIds: AnnotationLinkOrigin['id'][], targetKeys: LinkableTargetKey[]): AnnotationLinkCollection {
    const keys = targetKeys.map((key) => key.toString());
    return new AnnotationLinkCollection(
      this.links.filter((link) => fromIds.includes(link.fromId) || keys.includes(link.to.toString()))
    );
  }

  validBy(fromIds: AnnotationLinkOrigin['id'][], targetKeys: LinkableTargetKey[]): AnnotationLinkCollection {
    const keys = targetKeys.map((key) => key.toString());
    return new AnnotationLinkCollection(
      this.links.filter((link) => fromIds.includes(link.fromId) && keys.includes(link.to.toString()))
    );
  }

  merge(other: AnnotationLinkCollection): AnnotationLinkCollection {
    const ids = this.allIds();
    return new AnnotationLinkCollection(this.all().concat(other.all().filter(({ id }) => !ids.includes(id))));
  }

  cloneNew(
    fromIdMap: Record<AnnotationLinkOrigin['id'], AnnotationLinkOrigin['id']>,
    targetKeyMap: Record<string, LinkableTargetKey>
  ): AnnotationLinkCollection {
    return new AnnotationLinkCollection(this.links.map((link) => link.cloneNew(fromIdMap, targetKeyMap)));
  }

  buildCreateCommand(viewModelId: ViewModelId, modelId: ModelId): ICommand {
    return new CompositeCommand(...this.links.map((link) => link.buildCreateCommand(viewModelId, modelId)));
  }

  async buildDeleteCommand(viewModelId: ViewModelId, modelId: ModelId): Promise<ICommand | null> {
    if (this.isEmpty()) return null;

    const commands = await Promise.all(
      this.links.map(({ id }) =>
        CommandHelper.buildDeleteCommand(RTDBPath.AnnotationLink.linkPath(viewModelId, modelId, id))
      )
    );
    return CompositeCommand.composeOptionalCommands(...commands);
  }
}
